import { computeGuestPricePerNight } from '../pricing/pricing.util';
import { Boondock, HelperFunctions, IAmenity, ISite, ISubPhoto, Listing } from '@curbnturf/entities';
import {
  dateStringRangeToDateStringArray,
  getDayOfWeekDaysForNext3Years,
  getMonthDaysForNext3Years,
} from '@curbnturf/helpers';
import {
  AMENITY_LABELS,
  Amenity,
  Category,
  HookupAmps,
  IAddOn,
  IBoondockingListing,
  IHost,
  IListingDetail,
  IPrivateListing,
  ListingAgency,
  SewerType,
  amenityDetailsFrom,
  sortAmenities,
} from './mock-listings.data';
import { reverseCancellationPolicy } from './draft-listing-real-mapper';
import { addOnCapFromCount, addOnDisplayName, addOnUnitFromCount } from './add-on-pricing.util';

const AGENCY_VALUES: ListingAgency[] = ['BLM', 'USFS', 'NPS', 'State Park', 'Army Corps', 'County', 'Other'];
const AMENITY_VALUES = new Set(Object.keys(AMENITY_LABELS));

function toAgency(agency: string | undefined): ListingAgency {
  return (AGENCY_VALUES as string[]).includes(agency || '') ? (agency as ListingAgency) : 'Other';
}

function toLocation(city: string | undefined, state: string | undefined): string {
  if (city && state) return `${city}, ${state}`;
  return state || city || '';
}

function toAmenities(amenities: IAmenity[] | undefined): Amenity[] {
  if (!amenities) return [];
  const types = new Set<Amenity>();
  for (const a of amenities) {
    if (a.type && AMENITY_VALUES.has(a.type)) types.add(a.type as Amenity);
  }
  return sortAmenities([...types]);
}

/** Single site whose price/amenities represent the listing on the flat marketing shape —
 *  a listing can have multiple sites, so this picks the one site the rest of the app
 *  effectively treats as "the" listing. Mirrors client/libs/listing's
 *  main-view.component.ts setAvailableSites()/select-first-available pattern. */
function representativeSite(sites: ISite[] | undefined): ISite | undefined {
  if (!sites || sites.length === 0) return undefined;
  if (sites.length === 1) return sites[0];
  return sites.find((s) => s.available !== false) ?? sites[0];
}

/** Effective photo objects for a listing. Prefers `Listing.photos` (the web host
 *  wizard's write target as of HostListingDraftService.syncDraftPhotos, and also
 *  populated by the legacy mobile listing editor) but falls back to the union of
 *  every site's own `photos` for listings published before that change, whose real
 *  photos still live only on `listing.sites[*].photos`. Sites are visited in array
 *  order so the cover image stays deterministic.
 *
 *  Deliberately a different fallback shape from `realListingToDraft` (in
 *  draft-listing-real-mapper.ts), which reconstructs a single *editable* list and
 *  only falls back to the first site rather than unioning all of them — see that
 *  function's doc comment for why. */
function effectivePhotos(listing: Listing): ISubPhoto[] {
  if (listing.photos && listing.photos.length > 0) return listing.photos;
  return (listing.sites ?? []).flatMap((s) => s.photos ?? []);
}

/** Maps a real backend `Listing` entity (from `ListingFacade`) onto the flat
 *  `IPrivateListing` shape the guest-facing listing-details page is built around.
 *  Returns null when the entity has no usable identity (treat as not-found). */
export function toPrivateListingFromEntity(listing: Listing): IPrivateListing | null {
  if (!listing.id) return null;

  const site = representativeSite(listing.sites);
  const amenities = (listing.sites ?? []).flatMap((s) => toAmenities(s.amenities));
  const photos = effectivePhotos(listing);

  return {
    id: listing.id,
    title: listing.name || '',
    location: toLocation(listing.address?.city, listing.address?.state),
    city: listing.address?.city,
    state: listing.address?.state,
    coordinates: { lat: listing.coordinates?.lat ?? 0, lon: listing.coordinates?.lon ?? 0 },
    category: (listing.category || 'attraction') as Category,
    landTypes: listing.landTypes ?? [],
    // Re-sort: the union of several sites' (individually sorted) lists isn't.
    amenities: sortAmenities([...new Set(amenities)]),
    amenityDetails: amenityDetailsFrom((listing.sites ?? []).flatMap((s) => s.amenities ?? [])),
    image: HelperFunctions.getPhotoUrlSquareMedium(photos[0] ?? { key: '' }),
    images: photos.length > 0 ? photos.map((p) => HelperFunctions.getPhotoUrlSquareMedium(p)) : undefined,
    youtubeCode: listing.youtubeCode,
    rvcastUrl: listing.rvcastUrl,
    // The guest-facing basis, matching LiveSearchService and
    // `ISearchableListing.price`. `site.price` is the HOST's take-home; the
    // server charges it grossed up (`Site.getPrice()` / `getFee()`), so handing
    // the raw figure to a guest surface under-quotes by ~18%. Both are carried
    // now, each named for whose money it is.
    price: computeGuestPricePerNight(site?.price ?? 0),
    hostPrice: site?.price ?? 0,
    rating: listing.rating ?? 0,
    reviewCount: listing.ratingCount ?? 0,
    reservationProcess: listing.reservationProcess === 'instant' ? 'instant' : 'standard',
    available: !!listing.available,
    publishDate: listing.publishDate,
    taxes: (listing.taxes?.length ? listing.taxes : listing.listingTaxes?.map((lt) => lt.tax)) ?? [],
    // Left undefined when the host hasn't chosen one, so the card renders no chip
    // rather than a guessed tier. Unlike `toListingDetailFromEntity()` below, this
    // must NOT fall back to 'moderate': that would promise a refund window the
    // listing may not honour.
    cancellationTier: reverseCancellationPolicy(listing.cancellationPolicy),
    // Same rule: undefined beats a guess. `getListingDetail()`'s flat 35 was being
    // used for the card's fit verdict, which made every card agree regardless of
    // what the listing actually accepts.
    maxRigLength: maxRigLengthFromSites(listing.sites),
  };
}

/** "First L." — matches the shape the server's UserCleaningInterceptor already
 *  reduces a non-owner-visible user down to, so the client never claims to show
 *  more than a guest actually receives. */
function publicDisplayName(firstName: string | undefined, lastName: string | undefined): string {
  const lastInitial = lastName ? `${lastName[0]}.` : '';
  return [firstName, lastInitial].filter(Boolean).join(' ').trim();
}

function initialsOf(firstName: string | undefined, lastName: string | undefined): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`;
}

/** Real add-ons a host configured for this listing (`PUT /listing/:id/extras/`), mapped onto
 *  the marketing `IAddOn` shape. `icon` is a generic mark — the server has no column for one.
 *  `realExtraId` is populated so a booking built from these can reference the real extra, same
 *  as the host-side publish flow (`host-listing-draft.service.ts`'s `syncRealExtras`). Listings
 *  with no configured extras correctly return `[]` — no synthetic fallback.
 *
 *  `unit` used to be hardcoded 'per stay' for every real extra on the grounds that `count` is a
 *  stock cap rather than a guest-selectable quantity. But a cap of 2 on "Extra Vehicles" is
 *  precisely what makes it countable: the guest may buy one or two, at the stated price each.
 *  Treating a cap above 1 as 'per unit' is what resolves the "per vehicle or per stay?"
 *  ambiguity in Batch A #6, and it uses data the server already holds.
 *
 *  NOTE the server has no `unit` column at all, so a host who picks "per night" in the editor
 *  still loses that at publish. That needs one column added server-side; it is written up in
 *  the fixes doc rather than worked around here. */
export function addOnsFromEntity(listing: Listing): IAddOn[] {
  return (listing.extras ?? [])
    .filter((e) => e.id != null)
    .map((e) => ({
      id: `extra-${e.id}`,
      label: addOnDisplayName(e),
      description: e.description || '',
      icon: 'add_circle',
      price: e.price ?? 0,
      unit: addOnUnitFromCount(e.count),
      maxQuantity: addOnCapFromCount(e.count),
      realExtraId: e.id,
    }));
}

/**
 * Whether a mapped listing carries enough real data to be shown to a guest as a
 * bookable stay.
 *
 * `toPrivateListingFromEntity()` maps whatever the API hands back, defaulting the
 * missing parts (`name || ''`, `price || 0`). That is right for a mapper, but the
 * detail page was rendering the result unconditionally, so a listing with no name
 * and no price painted a full page: an empty `<h1>`, a title of "— | CurbNTurf", a
 * description reading "— a CurbNTurf stay in .", "0 amenities", orphaned "·"
 * separators, and a live Request-to-book button over "$0.00 / night".
 *
 * Signed-out visitors already got "Property not found" for exactly these listings —
 * the server withholds them. This makes the client agree rather than presenting a
 * shell as real inventory.
 *
 * Deliberately narrow: only the two fields the page cannot function without. A stay
 * with no name cannot be titled, linked or shared, and one with no price cannot be
 * booked. Everything else (missing photos, no amenities, no location) degrades into
 * an honest-looking page and is left alone.
 */
export function isRenderableListing(listing: IPrivateListing): boolean {
  // `available` is the backend's own "is this live/bookable" flag, false until
  // enable() succeeds. Without this check the detail page served listings the
  // host is being told are PAUSED: three half-published properties were sitting
  // at /rv-camping/... with a working Request-to-book CTA while /properties
  // badged every one of them PAUSED and offered RESUME. A host who pauses a
  // property to stop bookings would have kept receiving them.
  // Undefined means "no real backend flag to report" (see IPrivateListing) and
  // is treated as available, matching the convention used for sites.
  if (listing.available === false) return false;
  return listing.title.trim().length > 0 && listing.price > 0;
}

/** Real per-site specs (rig-length cap today — the only `ISiteSpecs` sub-field the backend
 *  actually models; see `toListingDetailFromEntity`'s doc comment for the rest). Picks
 *  the largest across the listing's bookable sites so the fit-check never under-reports what a
 *  guest's rig can actually fit. */
function maxRigLengthFromSites(sites: ISite[] | undefined): number | undefined {
  const lengths = (sites ?? []).map((s) => s.maxLength).filter((n): n is number => typeof n === 'number' && n > 0);
  return lengths.length > 0 ? Math.max(...lengths) : undefined;
}

/** Real per-site guest capacity — largest across the listing's bookable sites. */
function maxGuestsFromSites(sites: ISite[] | undefined): number | undefined {
  const counts = (sites ?? []).map((s) => s.maxGuests).filter((n): n is number => typeof n === 'number' && n > 0);
  return counts.length > 0 ? Math.max(...counts) : undefined;
}

/** Expands a `Listing`'s blackout config into a flat list of ISO blackout dates.
 *  Mirrors `Listing.getBlackoutDays()` (objects/src/classes/listing.ts) as a plain
 *  function instead of calling that class method directly — `listing` here is the
 *  raw HTTP JSON payload the NgRx store carries (`ListingFacade.selected$`), not a
 *  constructed `Listing` instance, so it has no class methods at runtime. Same
 *  pattern as `host-sites.service.ts`'s blackout-day expansion. */
function blackoutDaysFromListing(listing: Listing): string[] {
  const days: string[] = [];
  for (const range of listing.blackoutDays ?? []) days.push(...dateStringRangeToDateStringArray(range));
  for (const month of listing.blackoutMonths ?? []) days.push(...getMonthDaysForNext3Years(month));
  for (const dow of listing.blackoutDayOfWeek ?? []) days.push(...getDayOfWeekDaysForNext3Years(dow));
  return days;
}

/** Best electric/water/sewer hookup across a listing's bookable sites, decoded from each
 *  site's amenities. `electricity`'s `IAmenity.options` holds the amp rating as a string
 *  ('15'|'30'|'50' — see the host wizard's electricity controls in
 *  listing-amenities.component.html/phase1-amenities.component.ts and the guest-facing
 *  decoder in amenity-display.component.ts); `water`'s options ('direct'|'station')
 *  don't affect `waterAvailable`, just presence does — sewer has no options field at
 *  all: `sewage` present means full hookup, `dump` present without `sewage` means
 *  dump-station only, neither means none (mirrors `siteSpecsFromDraft`'s same
 *  derivation in mock-listings.data.ts). Aggregates the *best* found across sites,
 *  matching `maxRigLengthFromSites`/`maxGuestsFromSites`'s "most permissive across
 *  bookable sites" convention. */
function siteSpecsFromSites(sites: ISite[] | undefined): {
  hookupAmps: HookupAmps;
  waterAvailable: boolean;
  sewerType: SewerType;
} {
  const amenities = (sites ?? []).flatMap((s) => s.amenities ?? []);

  const amps = amenities
    .filter((a) => a.type === 'electricity')
    .map((a) => Number(a.options))
    .filter((n): n is 15 | 30 | 50 => n === 15 || n === 30 || n === 50);
  const hookupAmps: HookupAmps = amps.length > 0 ? (Math.max(...amps) as 15 | 30 | 50) : null;

  const waterAvailable = amenities.some((a) => a.type === 'water');

  const hasSewage = amenities.some((a) => a.type === 'sewage');
  const hasDump = amenities.some((a) => a.type === 'dump');
  const sewerType: SewerType = hasSewage ? 'full-hookup' : hasDump ? 'dump' : 'none';

  return { hookupAmps, waterAvailable, sewerType };
}

/** Builds the full `IListingDetail` for a listing fetched directly from the backend as a
 *  `Listing` entity — used by `listing-details.component.ts`'s `applyListing()`, which
 *  always has the entity in hand and therefore never needs `getListingDetail()`'s
 *  no-entity-in-hand fallback path (including its local-device "published snapshot"
 *  detour, which only ever fires for the same device that published the listing).
 *  `name`/`initials`/`avatar`/`joinedYear`/`hookupAmps`/`waterAvailable`/`sewerType` are
 *  sourced from the entity; `host.bio`, `responseHours`, `nearby`, `trustBadges`,
 *  `faqs`, `subScores`, `reviews`, and `siteSpecs.padType`/`padLength`/`leveling`/
 *  `slideoutClearance` have no backend concept at all and stay the same neutral
 *  placeholders `getListingDetail()` uses elsewhere. `listing` (the already-mapped
 *  `IPrivateListing`) supplies the description/photo fallback text/image. */
export function toListingDetailFromEntity(
  entity: Listing,
  listing: IPrivateListing,
): { detail: IListingDetail; siteCount: number; sites: ISite[] } {
  const photoObjs = effectivePhotos(entity);
  const photos =
    photoObjs.length > 0 ? photoObjs.map((p) => HelperFunctions.getPhotoUrlSquareLarge(p)) : [listing.image];
  const photoCaptions = photoObjs.length > 0 ? photoObjs.map((p) => p.caption ?? '') : [];

  const host: IHost = {
    name: 'Host',
    initials: '',
    avatar: listing.image,
    joinedYear: new Date().getFullYear(),
    bio: '',
    responseHours: 0,
  };
  if (entity.user) {
    host.name = publicDisplayName(entity.user.firstName, entity.user.lastName) || 'Host';
    host.initials = initialsOf(entity.user.firstName, entity.user.lastName);
    if (entity.user.photo) host.avatar = HelperFunctions.getPhotoUrlSquareSmall(entity.user.photo);
    // `created` is epoch seconds (see e.g. refer.component.ts's `new Date(u.created * 1000)`).
    if (entity.user.created) host.joinedYear = new Date(entity.user.created * 1000).getFullYear();
  }

  const maxRigLength = maxRigLengthFromSites(entity.sites) ?? 35;
  const { hookupAmps, waterAvailable, sewerType } = siteSpecsFromSites(entity.sites);

  const detail: IListingDetail = {
    description: entity.description || `${listing.title} — a CurbNTurf stay in ${listing.location}.`,
    host,
    photos,
    photoCaptions,
    houseRules: (entity.policies ?? []).map((p) => p.description).filter((d): d is string => !!d),
    cancellationTier: reverseCancellationPolicy(entity.cancellationPolicy) ?? 'moderate',
    maxGuests: maxGuestsFromSites(entity.sites) ?? 4,
    maxStayNights: entity.maxLengthOfStayInDays ?? 14,
    subScores: { cleanliness: 0, communication: 0, hookups: 0, location: 0, value: 0 }, // no backend concept yet
    reviews: [], // real reviews are layered on separately via ReviewService, not here
    nearby: [], // no backend concept yet
    trustBadges: [], // no backend concept yet
    unavailableDates: blackoutDaysFromListing(entity),
    siteSpecs: {
      padType: 'gravel',
      padLength: 35,
      leveling: 'mostly-level',
      slideoutClearance: 'moderate', // no backend concept yet
      maxRigLength,
      hookupAmps,
      waterAvailable,
      sewerType,
      bigRigFriendly: maxRigLength >= 38,
    },
    addOns: addOnsFromEntity(entity),
    faqs: [], // no backend concept yet
  };

  return {
    detail,
    siteCount: Math.max(1, entity.sites?.length ?? 1),
    // Real, bookable sites (with real `ISite.id`s) for this listing — lets the booking
    // flow pick/resolve a real `site.id` for `POST /reservation` instead of relying on
    // the publish-time snapshot, which only ever knows about self-published listings.
    // Filtered to `available` sites only (undefined treated as available, same convention
    // as `representativeSite()` above) so the guest never sees or can select an unbookable
    // site in the picker.
    sites: (entity.sites ?? []).filter((s) => s.available !== false),
  };
}

/** Real photos/description/policies/max-stay/rig-length for a fetched `Boondock` entity.
 *  Boondock has no host, site, cancellation-policy, blackout, or review concept — those
 *  `IListingDetail` fields have no backend source for boondocking (see `getListingDetail()`'s
 *  TODOs). */
export function boondockDetailOverridesFromEntity(
  boondock: Boondock,
  fallbackImage: string,
): {
  photos: string[];
  photoCaptions: string[];
  description?: string;
  houseRules: string[];
  maxStayNights?: number;
  maxRigLength?: number;
} {
  const photoObjs = boondock.photos ?? [];
  const photos =
    photoObjs.length > 0 ? photoObjs.map((p) => HelperFunctions.getPhotoUrlSquareLarge(p)) : [fallbackImage];
  const photoCaptions = photoObjs.length > 0 ? photoObjs.map((p) => p.caption ?? '') : [];
  return {
    photos,
    photoCaptions,
    description: boondock.description || undefined,
    houseRules: (boondock.policies ?? []).map((p) => p.description).filter((d): d is string => !!d),
    maxStayNights: boondock.maxLengthOfStayInDays,
    maxRigLength: boondock.maxLength,
  };
}

/** Maps a real backend `Boondock` entity (from `BoondockFacade`) onto the flat
 *  `IBoondockingListing` shape the boondock-details page is built around.
 *  Returns null when the entity has no usable identity (treat as not-found). */
export function toBoondockingListingFromEntity(boondock: Boondock): IBoondockingListing | null {
  if (!boondock.id) return null;

  return {
    id: boondock.id,
    title: boondock.name || '',
    location: toLocation(boondock.address?.city, boondock.address?.state),
    city: boondock.address?.city,
    state: boondock.address?.state,
    coordinates: { lat: boondock.coordinates?.lat ?? 0, lon: boondock.coordinates?.lon ?? 0 },
    category: 'offgrid',
    landTypes: boondock.landTypes ?? [],
    amenities: toAmenities(boondock.amenities),
    amenityDetails: amenityDetailsFrom(boondock.amenities ?? []),
    image: HelperFunctions.getPhotoUrlSquareMedium(boondock.photos?.[0] ?? { key: '' }),
    images:
      boondock.photos && boondock.photos.length > 0
        ? boondock.photos.map((p) => HelperFunctions.getPhotoUrlSquareMedium(p))
        : undefined,
    agency: toAgency(boondock.agency),
  };
}
